import {Injectable} from '@angular/core';
import {blank, DetailsItem, nameOnly, simple} from '@details/Interfaces/details-item-types';
import {LayoutOrientation} from '../../common/enums/layout-orientation';
import {DataFormatter} from '../../common/services/data-formatter.service';
import {Details} from '../../details/Interfaces/details';
import {LeaseDetails} from '../models/lease-details';
import {yOrN} from '@common/string-formatter/string-formatter';
import {DetailsBuilder} from '@details/Interfaces/details-builder';

@Injectable()
export class LeaseDetailsMapper {

  constructor() {
  }

  public map(details: LeaseDetails, dataFormatter: DataFormatter): Details {

    const breachStatus = details.BreachStatus.toLocaleLowerCase() !== 'none' ? ' - in breach' : '';
    const b = new DetailsBuilder(`Reference ${details.Ref}${breachStatus}`, details);

    let status = '';
    if (details.TerminationStatus.toLowerCase() === 'current') {
      status = 'current lease';
    } else {
      let today = new Date()
      let terminatedDate = new Date(details.TerminatedDate);
      if (terminatedDate > today) {
        status = 'current lease (termination date ' + dataFormatter.formatShortDate(terminatedDate) + ')';
      } else {
        status = 'non-current lease (termination date ' + dataFormatter.formatShortDate(terminatedDate) + ')';
      }
    }



    b.section(`This is a ${status} for:`, LayoutOrientation.Vertical)
      .conditionalItem(details.UnitName, nameOnly(details.UnitName))
      .for('UnitAddress', 'Address:')
      .for('TenantName', 'Tenant:');

    b.section('Key information:', LayoutOrientation.Horizontal)
      .for('LeaseTerm', 'The lease term is for:')
      .always(simple('Term starts on:', dataFormatter.formatDate(details.TermStartDate)))
      .always(simple('Term ends on:', dataFormatter.formatDate(details.TermEndDate)))
      .always(simple('and due to expire on:', dataFormatter.formatDate(details.ExpiryDate)));

    b.section('', LayoutOrientation.Horizontal)
      .always(simple('Annual rent is:', this.formatCurrencyOrNone(details.PassingRent, details, dataFormatter)))
      .always(simple('Annual service charge is:', this.formatCurrencyOrNone(details.AnnualServiceCharge, details, dataFormatter)))

    b.section('Additional information:', LayoutOrientation.Horizontal)
      .always(simple('The next review is due on:', this.formatDateOrNone(details.ReviewDate, dataFormatter)))
      .always(simple('The next option is due on:', this.formatDateOrNone(details.OptionDate, dataFormatter)));

    b.section('', LayoutOrientation.Horizontal)
      .conditionalItem(details.AssignmentPermitted, this.getAssignmentPermittedItem(details))
      .conditionalItem(details.SublettingPermitted, this.getSublettingPermittedItem(details))
      .conditionalItem(details.AlterationsPermitted, this.getAlterationsPermittedItem(details))
      .conditionalItem(details.RestrictiveConvenants, this.getRestrictiveCovenantsItem(details))
      .conditionalItem(details.ChangeOfUsePermitted, this.getChangeOfUserPermittedItem(details));

    return b.getDetails();
  }

  /**
   * @param currencyValue The numeric amount (as deserialized from the API call)
   * @param leaseDetails Used for the currency code suffix string
   * @param dataFormatter Uses Angular DecimalPipe for monetary amount formatting
   * @private
   */
  private formatCurrencyOrNone(currencyValue: Number, leaseDetails: LeaseDetails, dataFormatter: DataFormatter): string {
    return currencyValue
      ? `${dataFormatter.formatCurrency(currencyValue.toString())}${this.getCurrencyCodeSuffix(leaseDetails)}`
      : 'None';
  }

  /**
   * @param dateValue This could be an ISO string (as deserialized from the API call) or a native JS Date
   * (if supplied in .spec files for instance)
   * @param dataFormatter Uses Angular DatePipe for date formatting which can correctly process ISO date strings
   * or JS Date inputs.
   * @private
   */
  private formatDateOrNone(dateValue: Date | string, dataFormatter: DataFormatter): string {
    return dateValue
      ? dataFormatter.formatDate(dateValue)
      : 'None';
  }

  private getAssignmentPermittedItem(leaseDetails: LeaseDetails): DetailsItem {
    const permitted = yOrN(leaseDetails.AssignmentPermitted, 'can', 'cannot');
    if (!permitted) return blank();
    return nameOnly(`This lease ${permitted} be assigned.`);
  }

  private getSublettingPermittedItem(leaseDetails: LeaseDetails): DetailsItem {
    const permitted = yOrN(leaseDetails.SublettingPermitted, 'can', 'cannot');
    if (!permitted) return blank();
    return nameOnly(`This lease ${permitted} be sublet.`);
  }

  private getAlterationsPermittedItem(leaseDetails: LeaseDetails): DetailsItem {
    const allows = yOrN(leaseDetails.AlterationsPermitted, 'does', 'does not');
    if (!allows) return blank();
    return nameOnly(`This lease ${allows} allow alterations.`);
  }

  private getRestrictiveCovenantsItem(leaseDetails: LeaseDetails): DetailsItem {
    const are = yOrN(leaseDetails.RestrictiveConvenants, 'are', 'are no');
    if (!are) return blank();
    return nameOnly(`There ${are} restrictive covenants.`);
  }

  private getChangeOfUserPermittedItem(leaseDetails: LeaseDetails): DetailsItem {
    const permitted = yOrN(leaseDetails.ChangeOfUsePermitted, 'is', 'is not');
    if (!permitted) return blank();
    return nameOnly(`Change of use ${permitted} permitted.`);
  }

  private getCurrencyCodeSuffix(leaseDetails: LeaseDetails): string {
    return leaseDetails.DefaultCurrencyCode ? ' ' + leaseDetails.DefaultCurrencyCode : '';
  }
}
